import React, { useEffect, useMemo, useRef, useState } from "react";

import Footer from "./Footer";
import { useNavbarHeight } from "../utils/useNavbarHeight";

/**
 * Shell for the long-form pages: CBQs, Terms of Service, Privacy Policy. All
 * three had their own copy of this layout, its scroll spy and about 110 lines
 * of near-identical CSS, so a change to any of it had to be made three times.
 *
 * Callers pass `sections` as `[{ title, body }]` and own their own content
 * rendering. This component only does layout, the table of contents and the
 * spy, which is the part that was actually duplicated.
 */
const DocPage = ({ title, lead, meta, toolbar, sections, domain }) => {
  const [activeIdx, setActiveIdx] = useState(0);
  const [showTop, setShowTop] = useState(false);
  const sectionRefs = useRef([]);
  const tocRef = useRef(null);

  // Publishes --bl-navbar-h, which both the sticky rail's `top` and the
  // sections' `scroll-margin-top` read. They have to agree or a jumped-to
  // heading lands behind the bar.
  useNavbarHeight();

  // The observer is rebuilt only when the set of sections actually changes.
  // CBQs filters its list as you type, which hands us a new array on every
  // keystroke; keying off the titles keeps that from tearing down and
  // recreating an IntersectionObserver per character.
  const sectionKey = useMemo(
    () => sections.map((s) => s.title).join("|"),
    [sections],
  );

  useEffect(() => {
    const els = sectionRefs.current.slice(0, sections.length).filter(Boolean);
    if (!els.length) return;

    // Dense thresholds so a short section still reports a ratio change.
    const thresholds = Array.from({ length: 21 }, (_, i) => i / 20);

    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActiveIdx(Number(visible[0].target.dataset.idx));
      },
      // Watch a band across the middle of the viewport, not the whole thing.
      { root: null, rootMargin: "-35% 0px -55% 0px", threshold: thresholds },
    );

    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionKey, sections.length]);

  // The last section is usually short enough that it never fills the observer's
  // band, so scrolling to the very bottom would leave the previous one marked.
  useEffect(() => {
    const onScroll = () => {
      const atBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 2;
      if (atBottom && sections.length) setActiveIdx(sections.length - 1);
      setShowTop(window.scrollY > 400);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [sections.length]);

  // Keep the marked entry inside the sticky rail's own scroll box.
  useEffect(() => {
    const active = tocRef.current?.querySelector('[aria-current="true"]');
    active?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [activeIdx]);

  const goTo = (idx) => {
    // Set it here rather than waiting for the observer: smooth scrolling takes
    // a few hundred ms, and a nav item that does not respond to its own click
    // reads as broken.
    setActiveIdx(idx);
    document
      .getElementById(`doc-section-${idx}`)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="bleep-light bl-page bl-doc">
      <section className="bl-section">
        <div className="bl-shell">
          <div className="bl-feed-head">
            <h1 className="bl-page-title">{title}</h1>
            {lead && <p className="bl-page-sub">{lead}</p>}
            {meta && (
              <p className="bl-doc__meta">
                <i className="far fa-calendar" aria-hidden="true" />
                {meta}
              </p>
            )}
          </div>

          {toolbar}

          {/* Below the rail's breakpoint the same list becomes a jump menu. */}
          <div className="bl-doc__jump">
            <label className="bl-field__label" htmlFor="doc-jump">
              Jump to a section
            </label>
            <select
              id="doc-jump"
              className="bl-input"
              value={activeIdx}
              onChange={(e) => goTo(Number(e.target.value))}
            >
              {sections.map((section, idx) => (
                <option key={section.title} value={idx}>
                  {idx + 1}. {section.title}
                </option>
              ))}
            </select>
          </div>

          <div className="bl-doc__layout">
            <nav className="bl-doc__toc" aria-label="On this page" ref={tocRef}>
              <div className="bl-doc__toc-label">On this page</div>
              <ol className="bl-doc__toc-list">
                {sections.map((section, idx) => (
                  <li key={section.title}>
                    <button
                      type="button"
                      className="bl-doc__toc-link"
                      aria-current={activeIdx === idx ? "true" : undefined}
                      onClick={() => goTo(idx)}
                    >
                      <span className="bl-doc__toc-num">{idx + 1}</span>
                      <span>{section.title}</span>
                    </button>
                  </li>
                ))}
              </ol>
            </nav>

            <div className="bl-doc__body">
              {sections.length === 0 ? (
                <div className="bl-empty">
                  <div className="bl-empty__icon">
                    <i className="fas fa-magnifying-glass" aria-hidden="true" />
                  </div>
                  <h2 className="bl-empty__title">Nothing matched</h2>
                  <p className="bl-empty__text">
                    Try a shorter search, or clear it to see every section.
                  </p>
                </div>
              ) : (
                sections.map((section, idx) => (
                  <section
                    key={section.title}
                    id={`doc-section-${idx}`}
                    data-idx={idx}
                    ref={(el) => (sectionRefs.current[idx] = el)}
                    className="bl-doc__section"
                  >
                    <div className="bl-doc__card">
                      <h2 className="bl-doc__section-title">
                        <span className="bl-doc__section-num">{idx + 1}</span>
                        {section.title}
                      </h2>
                      {section.body}
                    </div>
                  </section>
                ))
              )}
            </div>
          </div>
        </div>
      </section>

      <button
        type="button"
        className={`bl-doc__top${showTop ? " bl-doc__top--in" : ""}`}
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        aria-hidden={showTop ? undefined : "true"}
        tabIndex={showTop ? undefined : -1}
      >
        <i className="fas fa-arrow-up" aria-hidden="true" />
        Top
      </button>

      <Footer domain={domain} />
    </div>
  );
};

export default DocPage;
